// The reaction chips under a message — port of ios/Sources/Views/ReactionChipsView.swift: count per
// emoji, the reader's own ringed in accent, click toggles, a trailing chip opens the full picker;
// hovering says who and when (the Mac's `.help(chip.tooltip)`), right-click (or a long press on a
// touch screen — the Apple gesture) opens the who-reacted dialog.
//
// `useReactionLayer` is everything a row needs to offer the picker and the dialog: open them from
// any control, and render `layer` (portals) next to the row.
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { ReactionChip } from '../../../core/reactionsDerivation';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import { EmojiGlyph, type CustomEmojiMap } from './EmojiGlyph';
import { EmojiPicker, type AnchorRect } from './EmojiPicker';
import { ReactionDetailsDialog } from './ReactionDetailsDialog';
import './emoji.css';

const LONG_PRESS_MS = 400;

export function ReactionChips({
  chips,
  custom,
  onToggle,
  onAdd,
  onDetails,
}: {
  chips: ReactionChip[];
  custom: CustomEmojiMap;
  onToggle(name: string): void;
  onAdd(anchor: AnchorRect): void;
  onDetails(emojiName: string): void;
}) {
  const press = useRef<{ timer: ReturnType<typeof setTimeout>; fired: boolean } | null>(null);
  const cancel = () => {
    if (press.current) clearTimeout(press.current.timer);
  };
  return (
    <div className="reactions">
      {chips.map((c) => (
        <button
          key={c.emojiName}
          type="button"
          className={`reaction${c.mine ? ' mine' : ''}`}
          title={c.tooltip || `:${c.emojiName}:`}
          aria-pressed={c.mine}
          aria-label={`:${c.emojiName}: ${c.count}`}
          onClick={() => {
            if (press.current?.fired) {
              press.current = null;
              return;
            }
            onToggle(c.emojiName);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDetails(c.emojiName);
          }}
          onPointerDown={(e) => {
            if (e.pointerType !== 'touch') return;
            cancel();
            const entry = { fired: false, timer: setTimeout(() => {
              entry.fired = true;
              onDetails(c.emojiName);
            }, LONG_PRESS_MS) };
            press.current = entry;
          }}
          onPointerUp={cancel}
          onPointerLeave={cancel}
          onPointerCancel={cancel}
        >
          <EmojiGlyph name={c.emojiName} custom={custom} />
          {c.count}
        </button>
      ))}
      <button
        type="button"
        className="reaction add"
        data-emoji-opener=""
        title={t('channel.addReaction')}
        aria-label={t('channel.addReaction')}
        onClick={(e) => onAdd(e.currentTarget.getBoundingClientRect())}
      >
        <Icon name="smilePlus" size={15} />
      </button>
    </div>
  );
}

export interface ReactionLayer {
  /** Opens (or, from the same opener, closes) the picker; a pick toggles that reaction. */
  openPicker(anchor: AnchorRect): void;
  /** The who-reacted dialog, on one emoji's tab or on «Todas». */
  openDetails(emojiName?: string): void;
  /** The picker or the dialog is up — the row keeps its hover bar visible meanwhile. */
  open: boolean;
  layer: ReactNode;
}

export function useReactionLayer(postId: string, onReact: (name: string) => void, custom: CustomEmojiMap): ReactionLayer {
  const [picker, setPicker] = useState<{ anchor: AnchorRect; startedAt: number } | null>(null);
  const [details, setDetails] = useState<{ emoji: string | null } | null>(null);
  const openPicker = useCallback((anchor: AnchorRect) => {
    const startedAt = performance.now();
    setPicker((current) => (current ? null : { anchor, startedAt }));
  }, []);
  const openDetails = useCallback((emojiName?: string) => setDetails({ emoji: emojiName ?? null }), []);
  const closeDetails = useCallback(() => setDetails(null), []);
  const layer =
    picker || details ? (
      <>
        {picker ? (
          <EmojiPicker
            anchor={picker.anchor}
            startedAt={picker.startedAt}
            title={t('channel.addReaction')}
            onPick={(name) => {
              setPicker(null);
              onReact(name);
            }}
            onClose={() => setPicker(null)}
          />
        ) : null}
        {/* A portal like the picker: the row's timeline would trap the scrim in its stacking context. */}
        {details ? createPortal(<ReactionDetailsDialog postId={postId} initial={details.emoji} custom={custom} onClose={closeDetails} />, document.body) : null}
      </>
    ) : null;
  return { openPicker, openDetails, open: !!picker || !!details, layer };
}
